// The single Supabase client for the whole app. Built from the PUBLIC publishable key
// (safe in client code — access is bounded by row-level security), with env overrides so a
// build can point at a staging project without a code change.
//
// OFFLINE-FIRST + SINGLE SIGN-IN: the session persists in chunked cookies on
// Domain=.seabearing.com (shared with seabearing.com and data.seabearing.com — sign in once,
// signed in everywhere) WITH a localStorage mirror on this origin, because Safari caps JS-set
// cookies at 7 days and a boat offline for a month must still restore its session. That cached
// session — plus the tier we cache alongside it (see ./store) — is what lets premium tiles
// unlock offline. The whole app must keep working signed-OUT; this module only ever ADDS
// capability, it never gates the chart.

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { createSharedStorage } from './sharedStorage';

export const SUPABASE_URL =
  (import.meta.env?.VITE_SUPABASE_URL as string | undefined) ?? 'https://ausolarpeuemimpmbiho.supabase.co';
const SUPABASE_ANON_KEY =
  (import.meta.env?.VITE_SUPABASE_ANON_KEY as string | undefined) ??
  'sb_publishable_CdUNKIy2P6cdyxFdbid4SQ_NcGzmvSg';

// Session storage key — MUST match the landing site (landing/auth.js) and the data explorer so
// the cookie session is one and the same everywhere. 'seabearing:auth' is the pre-SSO
// localStorage key; sessions stored under it are migrated on first read.
export const AUTH_STORAGE_KEY = 'seabearing-auth';

/** The persisted-session adapter. Exported so the auth store can read the stored session itself —
 *  with no network — to decide signed-in vs not (see ./store). */
export const authStorage = createSharedStorage({ mirror: true, legacyKeys: ['seabearing:auth'] });

// A token refresh may end the session ONLY on the auth server's own verdict about the refresh
// token. auth-js deletes the stored session (→ SIGNED_OUT) on any refresh error it does not class
// as retryable once the access token has expired (GoTrueClient _callRefreshToken). It classes a
// thrown fetch and the 5xx/52x codes as retryable (lib/fetch.ts _handleRequest / handleError), but
// a non-JSON 4xx — a marina captive portal's 511/403 page, a proxy's 407 — becomes an
// AuthUnknownError, and a 408/429 an AuthApiError: both would sign the boat out over a network
// condition. So for the refresh grant only, any failure that is not a 4xx JSON error from GoTrue
// (`code` / `error_code`, or the legacy `error`) is surfaced as a network failure, which auth-js
// retries and never acts on.
function isRefreshGrant(input: RequestInfo | URL): boolean {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  return url.includes('/auth/v1/token') && url.includes('grant_type=refresh_token');
}
async function isAuthServerVerdict(res: Response): Promise<boolean> {
  if (res.status < 400 || res.status >= 500 || res.status === 408 || res.status === 429) return false;
  try {
    const body = (await res.clone().json()) as Record<string, unknown> | null;
    return !!body && ['code', 'error_code', 'error'].some((k) => typeof body[k] === 'string');
  } catch {
    return false; // not JSON → not GoTrue
  }
}
async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (res.ok || !isRefreshGrant(input) || (await isAuthServerVerdict(res))) return res;
  throw new TypeError(`token refresh failed in transit (HTTP ${res.status})`);
}

export const supabase: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    // Stay signed in across reloads / restarts with no network round-trip.
    persistSession: true,
    autoRefreshToken: true,
    storage: authStorage,
    storageKey: AUTH_STORAGE_KEY,
    // Pick up the session from the URL fragment after a magic-link / OAuth redirect.
    detectSessionInUrl: true,
    // PKCE is the recommended browser flow (works for magic-link + OAuth without a secret).
    flowType: 'pkce',
  },
  // Every sub-client gets this fetch; it differs from the platform fetch only for a failed
  // refresh-token grant (see authFetch).
  global: { fetch: authFetch },
});
